import { mergeFileRecords } from "../../utils/fileIdentity"
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { compressImageFile } from "../../utils/imageCompression.js"
import { errorMessage } from "../../utils/mutationResult.js"
import { uploadFileDirectToS3 } from "../../utils/presignedUpload.js"
import { putOutboxItem, getAllOutboxItems, getCachedData } from "../../lib/offlineDb.js"
import { fetchOrQueue, cancelOutboxItemsForEntity } from "../../lib/offlineQueue.js"
import { upsertOutboxItem } from "./offlineSlice.js"
import { canonicalEntityId, resolveEntityId, isTemporaryEntityId, createReportReadGuard, readLocalReportEntities } from "../../lib/syncedEntityIds"
import { warmImageCache } from "../../lib/imageCache"
import { newKeyIssueId as newFileId } from "../../utils/keyIssueMatrix"

// Phase 2: a network failure (not a server-side rejection) means the device
// is offline. In that case we queue the upload instead of failing outright.
const isNetworkFailure = (error) =>
  (typeof navigator !== "undefined" && navigator.onLine === false) || error?.name === "TypeError"

const hasUnsyncedParent = (state, metadata) => String(metadata?.entityId).startsWith("offline") ||
  String(metadata?.reportId).startsWith("offline") ||
  (metadata?.entityType === "key-issue-matrix" &&
    Object.values(state?.auditReports?.allReportsData || {}).some((record) =>
      (record.keyIssueMatrixItems || []).some((item) => item._id === metadata.entityId && item._offlineQueued)))

// Initial state
const initialState = {
  files: [],
  currentFile: null,
  storageStats: null,
  uploadProgress: {},
  // Phase 1: per-file upload status queue, keyed by a client-generated id.
  // Shape: { [clientId]: { id, name, status: 'pending'|'uploading'|'uploaded'|'failed', error } }
  uploadQueue: {},
  pendingUpdates: {},
  loading: false,
  error: null,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  },
  filters: {
    projectId: "",
    fileType: "",
    entityType: "",
    entityId: "",
    uploadedBy: "",
  },
}

const makeClientId = (file) =>
  `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2)}`

const fileUpdateKey = ({ fileId, fileData }) => JSON.stringify([fileId, fileData])

// Phase 2 fix: an offline-queued upload previously returned a bare
// `{ _offlineQueued: true, name }` stub that couldn't render anywhere in the
// UI (it has no `relatedTo`, no image to display, nothing to match against
// the section filters). Build a real placeholder file record instead —
// shaped like a server file — using a local blob URL for the preview, so it
// shows up immediately in the same grid as synced images while it waits in
// the outbox.
const buildOfflinePlaceholderFile = (file, metadata, id, filterData) => {
  const previewUrl = typeof URL !== "undefined" && URL.createObjectURL ? URL.createObjectURL(file) : ""
  return {
    _id: `pending_${id}`,
    id: `pending_${id}`,
    _clientId: id,
    _offlineQueued: true,
    fileUrl: previewUrl,
    originalName: file.name,
    fileName: file.name,
    fileSize: file.size,
    fileType: file.type,
    mimeType: file.type,
    siteId: metadata?.siteId,
    fileDescription: "",
    uploadDate: new Date().toISOString(),
    reportId: metadata?.reportId || getActiveReportId(),
    relatedTo: {
      entityType: metadata?.entityType,
      entityId: metadata?.entityId,
      filterData: filterData ?? metadata?.filterData ?? {},
    },
  }
}

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8005/api"

// Keep the parent reference on queued edits/deletes even after the image is
// removed from Redux. Parent deletion can then cancel every image operation.
const findFileForQueue = (state, fileId) => [
  ...(state?.files?.files || []),
  ...(state?.auditReports?.currentReportData?.files || []),
  ...(state?.auditReports?.currentReportData?.report?.files || []),
  ...Object.values(state?.auditReports?.allReportsData || {}).flatMap((report) => report?.files || []),
].find((file) => [file?._id, file?.id, file?._clientId].includes(fileId))

  const getActiveReportId = () =>
  window.sessionStorage?.getItem("activeReportId") ||
  window.localStorage?.getItem("activeReportId") ||
  ""

  // Upload callers can retain the route props from before sync replaces an
  // offline site/report id with the server id. Resolve those ids from Redux
  // at the moment the payload is created, so the request never carries a
  // stale `offline-report-*` value even if the URL has already changed.
  const resolveSyncedId = (state, value, kind) => {
  if (typeof value !== "string") return value
  const prefixByKind = {
  report: "offline-report-",
  site: "offline-site-",
  work: "offline-work-",
  }
  const prefix = prefixByKind[kind]
  if (!prefix || !value.startsWith(prefix)) return value
  const records = kind === "report"
  ? [
  state?.auditReports?.currentReport,
  ...(state?.auditReports?.recentReports || []),
  ...Object.values(state?.auditReports?.allReportsData || {}).map((entry) => entry?.report),
  ]
  : kind === "work"
  ? state?.works?.works || []
  : [
  state?.siteData?.currentSiteData,
  ...(state?.siteData?.siteData || []),
  ]
  return records.find((record) =>
  (record?._clientId === value || record?._id === value || record?.id === value) &&
  record?._id &&
  !String(record._id).startsWith(prefix),
  )?._id || value
  }

  // Async thunks for files
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, Record<string, unknown> | void, {}>} */
export const fetchFiles = createAsyncThunk("files/fetchFiles", async (params = {}, { rejectWithValue, getState }) => {
  try {
    const state = getState()
    const reportId = canonicalEntityId(state, "report", params.reportId || getActiveReportId())
    const siteId = canonicalEntityId(state, "site", params.siteId)
    params = { ...params, ...(siteId ? { siteId } : {}) }
    const localDuringSync = createReportReadGuard(getState, reportId)
    if (localDuringSync()) return readLocalReportEntities(getState(), "files", reportId, siteId)
    if ((typeof navigator !== "undefined" && navigator.onLine === false) || isTemporaryEntityId(reportId) || isTemporaryEntityId(siteId)) {
      const record = state?.auditReports?.allReportsData?.[reportId] || state?.auditReports?.currentReportData ||
        (await getCachedData("audit-reports:all-with-data") || []).find((entry) => entry.report?._id === reportId)
      return (record?.files || state?.files?.files || []).filter((file) =>
        (!reportId || canonicalEntityId(state, "report", file.reportId) === reportId) &&
        (!params.siteId || !file.siteId || canonicalEntityId(state, "site", file.siteId) === params.siteId))
    }
    const queryParams = new URLSearchParams({
      ...params,
      ...(reportId ? { reportId } : {}),
    }).toString()
    const response = await fetch(`${API_BASE_URL}/files?${queryParams}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
        
      },
      credentials:"include"
    })

    if (!response.ok) {
      const error = await response.json()
      return rejectWithValue(errorMessage(error, "Failed to fetch files"))
    }

    const data = await response.json()
    await warmImageCache(Array.isArray(data.data) ? data.data : data.data?.files || [])
    if (localDuringSync()) return readLocalReportEntities(getState(), "files", reportId, siteId)
    return data.data
  } catch (error) {
    return rejectWithValue(errorMessage(error, "Network error"))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const fetchFileById = createAsyncThunk("files/fetchFileById", async (fileId, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/files/${fileId}?populate=true`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      return rejectWithValue(errorMessage(error, "Failed to fetch file"))
    }

    const data = await response.json()
    return data.data
  } catch (error) {
    return rejectWithValue(errorMessage(error, "Network error"))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, { file: File; metadata: Record<string, unknown>; clientId?: string }, {}>} */
export const uploadFile = createAsyncThunk(
  "files/uploadFile",
  async ({ file, metadata, clientId }, { rejectWithValue, dispatch, getState }) => {
  const id = clientId || makeClientId(file)
  const state = getState()
  const rawEntityId =
    metadata?.entityId ||
    (metadata?.entityType === "work" ? metadata?.workId || metadata?.itemId || metadata?.id : undefined)
  const resolvedMetadata = {
    ...metadata,
    siteId: resolveSyncedId(state, metadata?.siteId, "site"),
    reportId: resolveSyncedId(state, metadata?.reportId, "report"),
    entityId: resolveSyncedId(
      state,
      rawEntityId,
      metadata?.entityType === "report"
        ? "report"
        : metadata?.entityType === "work"
        ? "work"
        : "site",
    ),
  }
  metadata = resolvedMetadata
  dispatch(setFileQueueStatus({ id, name: file.name, status: "uploading" }))

    // Phase 1: if the device is already known to be offline, skip straight
    // to queuing instead of wasting time on a network call that can only
    // fail — mirrors the fast path fetchOrQueue already uses elsewhere.
    if ((typeof navigator !== "undefined" && navigator.onLine === false) || hasUnsyncedParent(state, metadata)) {
      try {
        const compressedFile = await compressImageFile(file)
        const placeholder = buildOfflinePlaceholderFile(compressedFile, metadata, id)
        const outboxItem = await putOutboxItem({
          id: `outbox_${id}`,
          type: "file-upload",
          file: compressedFile,
          metadata,
          clientId: id,
          meta: { description: `Upload ${file.name}`, entityType: metadata?.entityType },
        })
        dispatch(upsertOutboxItem(outboxItem))
        dispatch(setFileQueueStatus({ id, name: file.name, status: "queued" }))
        return placeholder
      } catch (queueError) {
        dispatch(setFileQueueStatus({ id, name: file.name, status: "failed", error: queueError.message }))
        return rejectWithValue(errorMessage(queueError, "Failed to queue offline upload"))
      }
    }

    try {
      // Phase 1: compress before upload, then retry transient network/5xx failures.
      const compressedFile = await compressImageFile(file)
      const reportId = metadata.reportId || getActiveReportId()

      // Phase 4: upload directly to S3 (single PUT or resumable multipart,
      // decided by the server based on file size) instead of routing bytes
      // through our server's memory as multipart/form-data.
      const data = await uploadFileDirectToS3(
          compressedFile,
          {
            siteId: metadata.siteId,
            reportId,
            entityType: metadata.entityType,
            entityId: metadata.entityId,
            filterData: metadata.filterData,
            metadata: { ...(metadata.metadata || {}), fileType: metadata.fileType, idempotencyKey: id },
          },
          { resumeKey: id },
      )

      dispatch(setFileQueueStatus({ id, name: file.name, status: "uploaded" }))
      return data
    } catch (error) {
      if (isNetworkFailure(error)) {
        // Phase 2: device is offline, not the server rejecting the upload.
        // Queue it in the outbox and let the auditor keep working — the
        // sync manager replays it once connectivity returns.
        try {
          const compressedFile = await compressImageFile(file)
          const placeholder = buildOfflinePlaceholderFile(compressedFile, metadata, id)
          const outboxItem = await putOutboxItem({
            id: `outbox_${id}`,
            type: "file-upload",
            file: compressedFile,
            metadata,
            clientId: id,
            meta: { description: `Upload ${file.name}`, entityType: metadata?.entityType },
          })
          dispatch(upsertOutboxItem(outboxItem))
          dispatch(setFileQueueStatus({ id, name: file.name, status: "queued" }))
          return placeholder
        } catch (queueError) {
          dispatch(setFileQueueStatus({ id, name: file.name, status: "failed", error: queueError.message }))
          return rejectWithValue(errorMessage(queueError, "Failed to queue offline upload"))
        }
      }
      dispatch(setFileQueueStatus({ id, name: file.name, status: "failed", error: error.message }))
      return rejectWithValue(errorMessage(error, "Network error"))
    }
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, { files: File[] | FileList; metadata: Record<string, unknown>; filterData?: Record<string, unknown> }, {}>} */
export const uploadMultipleFiles = createAsyncThunk(
  "files/uploadMultipleFiles",
  async ({ files, metadata, filterData = {} }, { rejectWithValue, dispatch }) => {
    const queueItems = Array.from(files || []).map((file) => ({ file, id: makeClientId(file) }))
    const results = new Array(queueItems.length)
    let next = 0
    // Limit compression and upload concurrency for mobile memory and bandwidth.
    const worker = async () => {
      while (next < queueItems.length) {
        const index = next++
        const { file, id } = queueItems[index]
        try {
          results[index] = { value: await dispatch(uploadFile({
            file, clientId: id, metadata: { ...metadata, filterData },
          })).unwrap() }
        } catch (error) {
          results[index] = { error: errorMessage(error), name: file.name }
        }
      }
    }
    await Promise.all(Array.from({ length: Math.min(2, queueItems.length) }, worker))
    const failures = results.filter((result) => result.error)
    if (failures.length) {
      if (results.length === 1) return rejectWithValue(failures[0].error)
      const saved = results.length - failures.length
      return rejectWithValue(
        failures.length + " image(s) failed; " + saved + " saved. " +
        failures.map((result) => result.name + ": " + result.error).join(". ") +
        " Retry only the failed images.",
      )
    }
    return results.map((result) => result.value)
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, { fileId: string; fileData: { fileDescription?: string; [key: string]: unknown } }, {}>} */
export const updateFile = createAsyncThunk(
  "files/updateFile",
  async ({ fileId, fileData }, { rejectWithValue, dispatch, getState }) => {
    try {
      console.log("updateFile called with fileId:", fileId, "fileData:", fileData);
      const requestedId = fileId
      fileId = await resolveEntityId(getState(), "file", fileId)
      // Description maps from an older render can still contain both IDs.
      // The pending key is obsolete once the server file exists; allowing
      // its old caption to save can overwrite a newer real-ID edit.
      if (requestedId !== fileId && Object.keys(fileData).every((field) => field === "fileDescription")) {
        const syncedFile = findFileForQueue(getState(), fileId)
        if (syncedFile) return syncedFile
      }
      const queuedFile = findFileForQueue(getState(), fileId)
      const requestUrl = `${API_BASE_URL}/files/${fileId}`
      const requestHeaders = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      }
      const requestBody = JSON.stringify({...fileData})

      // Phase 2: queue image description (and other metadata) edits in the
      // outbox when offline so they aren't silently dropped, and apply them
      // optimistically so the UI reflects the edit immediately.
      const outcome = await fetchOrQueue({
        dispatch,
        requestInit: { url: requestUrl, method: "PUT", headers: requestHeaders, body: requestBody },
        meta: {
          description: `Update file ${fileId}`,
          entityType: "file",
          entityId: fileId,
          requestType: "file-update",
          type: "file-update",
          parentEntityType: queuedFile?.relatedTo?.entityType || fileData?.relatedTo?.entityType,
          parentEntityId: queuedFile?.relatedTo?.entityId || fileData?.relatedTo?.entityId,
        },
        performRequest: async () => {
          const response = await fetch(requestUrl, {
            method: "PUT",
            headers: requestHeaders,
            body: requestBody,
            credentials: "include",
          })
          if (!response.ok) {
            const error = await response.json()
            const err = new Error(errorMessage(error, "Failed to update file"))
            err.status = response.status
            throw err
          }
          return response.json()
        },
      })

      if (outcome.queued) {
        // The observation screen reads refreshed files from auditReports rather
        // than the standalone files slice. Update that source too, otherwise
        // the optimistic file-slice result is immediately hidden on rerender.
        dispatch({
          type: "auditReports/updateCurrentReportFile",
          payload: { fileId, fileData },
        })
        const state = getState()
        const existing = [
          ...(state?.files?.files || []),
          ...(state?.auditReports?.currentReportData?.files || []),
          ...(state?.auditReports?.currentReportData?.report?.files || []),
        ].find(
          (file) =>
            String(file?.id || "") === String(fileId) ||
            String(file?._id || "") === String(fileId) ||
            String(file?._clientId || "") === String(fileId),
        )
        return {
          ...(existing || {}),
          ...fileData,
          _id: existing?._id || fileId,
          id: existing?.id || fileId,
          _offlineQueued: true,
        }
      }

      const data = outcome.result
      return data.data
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"))
    }
  },
  {
    condition: ({ fileId, fileData }, { getState }) => {
      const state = getState()
      fileId = canonicalEntityId(state, "file", fileId)
      if (Object.keys(state.files?.pendingUpdates || {}).some((key) => {
        const [pendingId, pendingData] = JSON.parse(key)
        return canonicalEntityId(state, "file", pendingId) === fileId &&
          JSON.stringify(pendingData) === JSON.stringify(fileData)
      })) return false
      const existingFile = findFileForQueue(state, fileId)

      // A delayed autosave may still carry a deleted placeholder ID. It has
      // no server record to update and must never recreate an orphan PUT.
      if (!existingFile && isTemporaryEntityId(fileId)) return false
      if (!existingFile) return true

      return Object.entries(fileData).some(
        ([field, value]) =>
          JSON.stringify(existingFile[field]) !== JSON.stringify(value),
      )
    },
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const cloneFiles = createAsyncThunk(
  "files/cloneFiles",
  async ({ files, siteId, reportId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const result = []
      for (const file of files) {
        const clientId = newFileId()
        const url = `${API_BASE_URL}/files/clone`
        const headers = { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` }
        const body = JSON.stringify({ files: [{ ...file, clientFileId: clientId }], siteId, reportId })
        const parentItems = Object.values(getState()?.auditReports?.allReportsData || {}).flatMap((record) => record.keyIssueMatrixItems || [])
        const pendingParent = parentItems.some((item) => item._id === file.relatedTo?.entityId && item._offlineQueued)
        const outcome = await fetchOrQueue({ dispatch, requestInit: { url, method: "POST", headers, body },
          forceQueue: pendingParent || String(reportId).startsWith("offline") || String(file.relatedTo?.entityId).startsWith("offline"),
          meta: { entityType: "file", entityId: clientId, clientId, requestType: "file-clone", reportId,
            parentEntityType: file.relatedTo?.entityType, parentEntityId: file.relatedTo?.entityId,
            description: `Import image ${file.originalName || file.fileName || clientId}` },
          performRequest: async () => {
            const response = await fetch(url, { method: "POST", headers, body, credentials: "include" })
            const payload = await response.json()
            if (!response.ok) throw Object.assign(new Error(errorMessage(payload, "Failed to clone files")), { status: response.status })
            return payload.data
          },
        })
        if (outcome.queued) result.push({ ...file, _id: clientId, id: clientId, _clientId: clientId,
          reportId, siteId, isImported: false, _offlineQueued: true,
          metadata: { ...file.metadata, importedFromFileId: file.sourceFileId || file._id || file.id } })
        else result.push(...outcome.result)
      }
      return result
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"))
    }
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const deleteFile = createAsyncThunk("files/deleteFile", async (fileId, { rejectWithValue, dispatch, getState }) => {
  try {
    const reportId =
      window.sessionStorage?.getItem("activeReportId") ||
      window.localStorage?.getItem("activeReportId") ||
      "";
    const reportFiles =
      getState()?.auditReports?.allReportsData?.[reportId]?.files ||
      getState()?.auditReports?.currentReportData?.files ||
      [];
    const pendingFile = reportFiles.find(
      (file) => (file?._id || file?.id) === fileId && file?.isImported,
    );

    if (pendingFile) {
      const { removePendingImportedFileFromReport } = await import("./auditReportSlice.js");
      dispatch(removePendingImportedFileFromReport({ reportId, fileId }));
      return fileId;
    }

    const stateFile = findFileForQueue(getState(), fileId)

    // Phase 2: this image is still an unsynced offline upload sitting in the
    // outbox — the server has never heard of it, so deleting it means
    // cancelling the queued upload rather than sending a delete request for
    // a file id that doesn't exist server-side.
    if (stateFile?._offlineQueued && stateFile?._clientId) {
      await cancelOutboxItemsForEntity(dispatch, { clientId: stateFile._clientId })
      if (typeof stateFile.fileUrl === "string" && stateFile.fileUrl.startsWith("blob:")) {
        URL.revokeObjectURL(stateFile.fileUrl)
      }
      return fileId
    }

    const requestUrl = `${API_BASE_URL}/files/${fileId}`
    const requestHeaders = { Authorization: `Bearer ${localStorage.getItem("token")}` }

    // Phase 2: queue the delete in the outbox when offline so it replays
    // once connectivity returns, and remove it optimistically in the
    // meantime so the UI reflects the deletion right away.
    await fetchOrQueue({
      dispatch,
      requestInit: { url: requestUrl, method: "DELETE", headers: requestHeaders },
      meta: {
        description: `Delete file ${fileId}`,
        entityType: "file",
        entityId: fileId,
        requestType: "file-delete",
        type: "file-delete",
        parentEntityType: stateFile?.relatedTo?.entityType,
        parentEntityId: stateFile?.relatedTo?.entityId,
      },
      performRequest: async () => {
        const response = await fetch(requestUrl, {
          method: "DELETE",
          headers: requestHeaders,
          credentials: "include",
        })
        if (!response.ok) {
          const error = await response.json()
          const err = new Error(errorMessage(error, "Failed to delete file"))
          err.status = response.status
          throw err
        }
        return fileId
      },
    })

    return fileId
  } catch (error) {
    return rejectWithValue(errorMessage(error, "Network error"))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const archiveFile = createAsyncThunk("files/archiveFile", async (fileId, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/files/${fileId}/archive`, {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      return rejectWithValue(errorMessage(error, "Failed to archive file"))
    }

    const data = await response.json()
    return data.data
  } catch (error) {
    return rejectWithValue(errorMessage(error, "Network error"))
  }
})

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const fetchFilesByEntity = createAsyncThunk(
  "files/fetchFilesByEntity",
  async ({ entityType, entityId, fileType }, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams()
      if (fileType) queryParams.append("fileType", fileType)

      const response = await fetch(`${API_BASE_URL}/files/entity/${entityType}/${entityId}?${queryParams.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })

      if (!response.ok) {
        const error = await response.json()
        return rejectWithValue(errorMessage(error, "Failed to fetch entity files"))
      }

      const data = await response.json()
      return data.data
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Network error"))
    }
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const fetchStorageStats = createAsyncThunk("files/fetchStorageStats", async (projectId, { rejectWithValue }) => {
  try {
    const url = projectId ? `${API_BASE_URL}/files/stats/${projectId}` : "${API_BASE_URL}/files/stats"
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      return rejectWithValue(errorMessage(error, "Failed to fetch storage statistics"))
    }

    const data = await response.json()
    return data.data
  } catch (error) {
    return rejectWithValue(errorMessage(error, "Network error"))
  }
})

// Phase 3: on app boot, any file the user uploaded while offline is sitting
// in the IndexedDB outbox as a raw File object — the blob preview URL that
// rendered it in the UI died with the previous page load (blob: URLs are
// per-session). Rebuild a fresh blob URL for each queued upload and merge
// it back in as a placeholder so offline-uploaded images keep rendering
// after a refresh, even before connectivity (and the real sync) returns.
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const hydratePendingFileUploads = createAsyncThunk(
  "files/hydratePendingFileUploads",
  async (_, { dispatch }) => {
    const items = await getAllOutboxItems()
    const placeholders = items
      .filter((item) => item.type === "file-upload" && item.file)
      .map((item) => buildOfflinePlaceholderFile(item.file, item.metadata, item.clientId, item.filterData))
    placeholders.forEach((placeholder) => dispatch(hydrateOfflineFile(placeholder)))
    return placeholders
  },
)

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const getDownloadUrl = createAsyncThunk("files/getDownloadUrl", async (fileId, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/files/${fileId}/download`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      return rejectWithValue(errorMessage(error, "Failed to get download URL"))
    }

    const data = await response.json()
    return data.data
  } catch (error) {
    return rejectWithValue(errorMessage(error, "Network error"))
  }
})

// File slice
const fileSlice = createSlice({
  name: "files",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setCurrentFile: (state, action) => {
      state.currentFile = action.payload
    },
    clearCurrentFile: (state) => {
      state.currentFile = null
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {
        projectId: "",
        fileType: "",
        entityType: "",
        entityId: "",
        uploadedBy: "",
      }
    },
    setUploadProgress: (state, action) => {
      const { fileId, progress } = action.payload
      state.uploadProgress[fileId] = progress
    },
    clearUploadProgress: (state, action) => {
      const fileId = action.payload
      delete state.uploadProgress[fileId]
    },
    clearAllUploadProgress: (state) => {
      state.uploadProgress = {}
    },
    setFilesFromVersion: (state, action) => {
      // Used when loading historical version data
      state.files = action.payload
    },
    // Phase 1: per-file upload status queue.
    setFileQueueStatus: (state, action) => {
      const { id, name, status, error } = action.payload
      state.uploadQueue[id] = { id, name, status, error: error || null }
    },
    clearFileQueueItem: (state, action) => {
      delete state.uploadQueue[action.payload]
    },
    clearCompletedFileQueueItems: (state) => {
      Object.keys(state.uploadQueue).forEach((id) => {
        if (state.uploadQueue[id].status === "uploaded") {
          delete state.uploadQueue[id]
        }
      })
    },
    retryFileQueueItem: (state, action) => {
      const id = action.payload
      if (state.uploadQueue[id]) {
        state.uploadQueue[id].status = "pending"
        state.uploadQueue[id].error = null
      }
    },
    // Phase 2: called once a queued outbox file-upload item successfully
    // reaches S3 + the DB during a manual sync. Replaces the offline
    // placeholder (matched by `_clientId`) with the real file record so the
    // list reflects reality in place, instead of ending up with both the
    // stale placeholder and the synced record shown at once.
    addSyncedFile: (state, action) => {
      const { file, clientId } = action.payload || {}
      if (!file) return
      const syncedFile = clientId ? { ...file, _clientId: clientId } : file
      state.files.forEach(placeholder => {
        if (clientId && placeholder._clientId === clientId && placeholder.fileUrl?.startsWith("blob:") && placeholder.fileUrl !== file.fileUrl) {
          URL.revokeObjectURL(placeholder.fileUrl)
        }
      })
      state.files = mergeFileRecords(state.files, [syncedFile])
    },
    // Phase 3: reinsert a still-queued offline upload's placeholder after a
    // page reload, replacing expired cached blob URLs without duplicating
    // the image in the grid.
    hydrateOfflineFile: (state, action) => {
      const placeholder = action.payload
      if (!placeholder) return
      const index = state.files.findIndex((f) => f._clientId === placeholder._clientId)
      if (index === -1) {
        state.files.unshift(placeholder)
      } else if (state.files[index]._offlineQueued) {
        // Cached blob URLs belong to the previous page session. Replace
        // them with the fresh preview reconstructed from the queued File.
        const existing = state.files[index]
        state.files[index] = { ...existing, ...placeholder, fileDescription: existing.fileDescription || placeholder.fileDescription }
      }
    },
    // Called when a parent entity (e.g. an observation) is deleted offline
    // and its still-queued image uploads are cancelled along with it — those
    // placeholder files can never sync now, so drop them from the grid too.
    removePendingFilesByClientIds: (state, action) => {
      const clientIds = action.payload || []
      if (clientIds.length === 0) return
      state.files = state.files.filter((f) => {
        if (!f._clientId || !clientIds.includes(f._clientId)) return true
        if (typeof f.fileUrl === "string" && f.fileUrl.startsWith("blob:")) {
          URL.revokeObjectURL(f.fileUrl)
        }
        return false
      })
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch files cases
      .addCase(fetchFiles.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFiles.fulfilled, (state, action) => {
        state.loading = false
        // Offline reads return the cached array; online reads include a
        // paginated envelope. Both must leave an array for upload reducers.
        state.files = Array.isArray(action.payload) ? action.payload :
          Array.isArray(action.payload?.files) ? action.payload.files : []
        if (action.payload?.pagination) state.pagination = action.payload.pagination
        state.error = null
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Fetch file by ID cases
      .addCase(fetchFileById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchFileById.fulfilled, (state, action) => {
        state.loading = false
        state.currentFile = action.payload
        state.error = null
      })
      .addCase(fetchFileById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Upload file cases
      .addCase(uploadFile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.loading = false
        // Phase 2: an offline-queued upload now returns a real placeholder
        // file record (with a local blob preview URL), so it renders in the
        // grid immediately instead of only appearing once synced.
        if (action.payload) {
          state.files = mergeFileRecords(state.files, [action.payload])
        }
        state.error = null
      })
      .addCase(uploadFile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Upload multiple files cases
      .addCase(uploadMultipleFiles.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(uploadMultipleFiles.fulfilled, (state, action) => {
        state.loading = false
        state.files = mergeFileRecords(state.files, action.payload)
        state.error = null
      })
      .addCase(uploadMultipleFiles.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Update file cases
      .addCase(updateFile.pending, (state, action) => {
        state.pendingUpdates[fileUpdateKey(action.meta.arg)] = true
        state.loading = true
        state.error = null
      })
      .addCase(updateFile.fulfilled, (state, action) => {
        delete state.pendingUpdates[fileUpdateKey(action.meta.arg)]
        state.loading = false
        const requestedFileId = action.meta?.arg?.fileId
        const payload = action.payload || {}
        const matchesFile = (file) =>
          String(file?._id || file?.id || file?._clientId || "") ===
            String(requestedFileId || "") ||
          (payload._id && String(file?._id || "") === String(payload._id)) ||
          (payload.id && String(file?.id || "") === String(payload.id))

        const index = state.files.findIndex(matchesFile)
        if (index !== -1) {
          // Merge into the existing record so an offline description update
          // cannot drop its preview URL, relation, or local upload metadata.
          state.files[index] = { ...state.files[index], ...payload }
        }
        if (state.currentFile && matchesFile(state.currentFile)) {
          state.currentFile = { ...state.currentFile, ...payload }
        }
        state.error = null
      })
      .addCase(updateFile.rejected, (state, action) => {
        delete state.pendingUpdates[fileUpdateKey(action.meta.arg)]
        state.loading = false
        state.error = action.payload
      })

      // Clone files cases
      .addCase(cloneFiles.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(cloneFiles.fulfilled, (state, action) => {
        state.loading = false
        const clonedFiles = action.payload || []
        state.files = state.files.filter((existing) => {
          if (!existing?.isImported) return true
          return !clonedFiles.some((cloned) => {
            const importedFromFileId = cloned?.metadata?.importedFromFileId
            const sameSource =
              String(existing?.sourceFileId || "") ===
              String(importedFromFileId || "")
            const sameEntity =
              existing?.relatedTo?.entityType === cloned?.relatedTo?.entityType &&
              (String(existing?.relatedTo?.entityId || "") ===
                String(cloned?.relatedTo?.entityId || "") ||
                String(existing?.relatedTo?.entityId || "").startsWith("imported-"))
            const sameFilter =
              JSON.stringify(existing?.relatedTo?.filterData || {}) ===
              JSON.stringify(cloned?.relatedTo?.filterData || {})
            return sameSource && sameEntity && sameFilter
          })
        })
        state.files = [...clonedFiles, ...state.files]
        state.error = null
      })
      .addCase(cloneFiles.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Delete file cases
      .addCase(deleteFile.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteFile.fulfilled, (state, action) => {
        state.loading = false
        state.files = state.files.filter((f) => f._id !== action.payload)
        if (state.currentFile && state.currentFile._id === action.payload) {
          state.currentFile = null
        }
        state.error = null
      })
      .addCase(deleteFile.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

      // Archive file cases
      .addCase(archiveFile.fulfilled, (state, action) => {
        const index = state.files.findIndex((f) => f._id === action.payload._id)
        if (index !== -1) {
          state.files[index] = action.payload
        }
        if (state.currentFile && state.currentFile._id === action.payload._id) {
          state.currentFile = action.payload
        }
      })

      // Fetch files by entity cases
      .addCase(fetchFilesByEntity.fulfilled, (state, action) => {
        // This could be used to populate a specific entity's files
        // For now, we'll just update the main files array
        state.files = action.payload
      })

      // Fetch storage stats cases
      .addCase(fetchStorageStats.fulfilled, (state, action) => {
        state.storageStats = action.payload
      })
  },
})

export const {
  clearError,
  setCurrentFile,
  clearCurrentFile,
  setFilters,
  clearFilters,
  setUploadProgress,
  clearUploadProgress,
  clearAllUploadProgress,
  setFilesFromVersion,
  setFileQueueStatus,
  clearFileQueueItem,
  clearCompletedFileQueueItems,
  retryFileQueueItem,
  addSyncedFile,
  hydrateOfflineFile,
  removePendingFilesByClientIds,
} = fileSlice.actions

export default fileSlice.reducer
